import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * Lost in Migration — vue React staircase + debrief.
 *
 * Rendu : 5 oiseaux (Unicode flèches) en ligne horizontale ou verticale selon
 * `stim.axis`. Le central est légèrement mis en valeur. Pendant
 * `awaiting_input`, on affiche 2 ou 4 boutons directionnels selon nDirections.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig, } from './params';
const ARROW = {
    left: '←',
    right: '→',
    up: '↑',
    down: '↓',
};
function birdsForStim(central, flanker) {
    return [flanker, flanker, central, flanker, flanker];
}
function activeDirections(variant) {
    return variant.nDirections === 2
        ? ['left', 'right']
        : ['left', 'right', 'up', 'down'];
}
export function LostInMigrationStaircaseView({ adapter, level: _level, onAbort, }) {
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handlePick = useCallback((d) => adapter.handleAnswer(d), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const stim = adapterState.stim;
    const showBirds = adapterState.subPhase === 'stimulus' ||
        adapterState.subPhase === 'awaiting_input';
    const birds = stim
        ? birdsForStim(stim.centralDirection, stim.flankerDirection)
        : [];
    const isVertical = stim?.axis === 'vertical';
    const dirs = activeDirections(adapterState.variant);
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsxs("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: ["Essai ", Math.min(trialDisplay, staircaseConfig.maxTrials), "/", staircaseConfig.maxTrials] }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: "Arr\u00EAter" })] }), _jsxs("div", { className: "relative flex h-[280px] w-full max-w-[340px] items-center justify-center border border-cyan-border-faint bg-cyan-surface", children: [adapterState.subPhase === 'fixation' && (_jsx("span", { className: "font-display text-[48px] text-cyan-text", children: "+" })), showBirds && stim && (_jsx("div", { className: [
                            'flex items-center',
                            isVertical ? 'flex-col gap-[12px]' : 'flex-row gap-[12px]',
                        ].join(' '), children: birds.map((d, i) => (_jsx("span", { className: [
                                'font-display text-[50px] leading-none',
                                i === 2 ? 'text-cyan-text' : 'text-cyan-text-secondary',
                            ].join(' '), children: ARROW[d] }, i))) })), adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                            'absolute inset-0 flex items-center justify-center font-display text-[40px] font-medium',
                            adapterState.feedback === 'correct'
                                ? 'text-cyan-core'
                                : 'text-cyan-media',
                        ].join(' '), style: { background: 'rgba(255,255,255,0.85)' }, children: adapterState.feedback === 'correct' ? '✓' : '✗' }))] }), _jsx("div", { className: "text-[11px] text-cyan-text-secondary text-center", children: "Pointe la direction de l\u2019oiseau central. Ignore les flankers." }), adapterState.subPhase === 'awaiting_input' && (_jsx("div", { className: [
                    'flex justify-center',
                    adapterState.variant.nDirections === 4 ? 'gap-s3' : 'gap-s4',
                ].join(' '), children: dirs.map((d) => (_jsx("button", { type: "button", onClick: () => handlePick(d), "aria-label": `Direction ${d}`, className: "flex h-[72px] w-[72px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[36px] text-cyan-text transition duration-feedback ease-soft-wind hover:bg-cyan-bg active:scale-95", children: ARROW[d] }, d))) }))] }));
}
export function LostInMigrationDebriefView({ level: _level, onAbort: _onAbort, }) {
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const fastestRT = useMemo(() => {
        const correctRTs = staircase.trialLog
            .filter((tt) => tt.response.correct)
            .map((tt) => tt.response.reactionTimeMs);
        if (correctRTs.length === 0)
            return null;
        return Math.min(...correctRTs);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : `${Math.round(threshold)} ms`;
    const recommendation = (() => {
        if (trialsCompleted === 0) {
            return 'Reviens demain pour 3 minutes — la consolidation passe par le sommeil.';
        }
        if (threshold !== null && threshold <= 250) {
            return 'Filtrage rapide. Continue à courte durée pour progresser.';
        }
        if (threshold !== null && threshold <= 500) {
            return 'Filtrage stable. Reviens demain pour solidifier.';
        }
        return 'Conflit cognitif normal. La régularité fait progresser.';
    })();
    return (_jsx(DebriefShell, { title: "Migration", scoreLabel: "Seuil de dur\u00E9e", scoreValue: thresholdLabel, fallbackBadge: fallback, metrics: [
            { label: 'Essais', value: trialsCompleted },
            { label: 'Justes', value: `${accuracy}%` },
            {
                label: 'RT min',
                value: fastestRT === null ? '—' : `${Math.round(fastestRT)} ms`,
            },
        ], recommendation: recommendation, ctaReplay: _jsx("button", { type: "button", onClick: () => window.location.reload(), className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: "Rejouer" }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: "Choisir un autre jeu" }) }));
}
//# sourceMappingURL=staircaseView.js.map